import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AttendanceService from '../services/AttendanceService';
import { FaceDetectionService } from '../services/FaceDetectionService';
import { geofenceService } from '../services/GeofenceService';
import { locationService } from '../services/LocationService';
import './AttendanceScreen.css';

const AttendanceScreen: React.FC = () => {
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [location, setLocation] = useState<GeolocationPosition | null>(null);
  const [isInArea, setIsInArea] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState<string>('outside');
  const [isScanning, setIsScanning] = useState(false);
  const [punchCode, setPunchCode] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [detectedFaces, setDetectedFaces] = useState<any[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    initializeApp();
    return () => {
      locationService.stopLocationTracking();
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(console.error);
      };
    }
  }, [cameraStream]);

  const initializeApp = async () => {
    try {
      // Request location permission
      const locationPermission = await locationService.requestPermission();
      
      if (locationPermission) {
        setHasPermission(true);
        startLocationTracking();
      } else {
        setHasPermission(false);
        alert('Location permission is required for attendance tracking.');
      }
    } catch (error) {
      console.error('Error initializing app:', error);
      setHasPermission(false);
    }
  };

  const startLocationTracking = async () => {
    try {
      const initialLocation = await locationService.getCurrentLocation();
      setLocation(initialLocation);
      checkGeofence(initialLocation);

      locationService.startLocationTracking((newLocation) => {
        console.log('Location updated:', newLocation.coords);
        setLocation(newLocation);
        checkGeofence(newLocation);
      });
    } catch (error) {
      console.error('Error starting location tracking:', error);
    }
  };

  const checkGeofence = async (currentLocation: GeolocationPosition) => {
    try {
      const inArea = await geofenceService.isInDesignatedArea(currentLocation);
      console.log(`Geofence check: ${inArea ? 'INSIDE' : 'OUTSIDE'} area`);

      if (inArea && !isInArea) {
        console.log('ENTERING designated area');
        setIsInArea(true);
        setAttendanceStatus('entering');
      } else if (!inArea && isInArea) {
        console.log('EXITING designated area');
        setIsInArea(false);
        setAttendanceStatus('exiting');
        await handleAreaExit(currentLocation);
      }
    } catch (error) {
      console.error('Error in geofence check:', error);
    }
  };

  const handleAreaExit = async (currentLocation: GeolocationPosition) => {
    if (!punchCode || punchCode.trim() === '') {
      alert('Please enter a punch code before leaving the area.');
      return;
    }

    console.log('Area exit with location:', currentLocation.coords);
    alert('Leaving Designated Area - Marking exit attendance...');

    try {
      const exitResult = await AttendanceService.markExit(currentLocation, punchCode);
      alert('Exit marked successfully!');
    } catch (error) {
      console.error('Error marking exit:', error);
      alert('Error marking exit. Please try again.');
    }
  };

  const captureImage = (): string | null => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas not available');
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Could not get canvas context');
      return null;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to base64
    return canvas.toDataURL('image/jpeg', 0.7).split(',')[1]; // Remove data:image/jpeg;base64, prefix
  };

  const scanFaces = async () => {
    if (!punchCode || punchCode.trim() === '') {
      alert('Please enter a punch code before scanning.');
      return;
    }

    if (!location) {
      alert('Location not available. Please wait for location to be determined.');
      return;
    }

    if (!showCamera || !cameraStream) {
      alert('Please start the camera first.');
      return;
    }

    setIsScanning(true);

    try {
      console.log('=== STARTING CANDIDATE VERIFICATION ===');
      console.log('SCAN: Using punch code:', punchCode);

      // Capture image from camera
      const imageBase64 = captureImage();
      if (!imageBase64) {
        throw new Error('Failed to capture image from camera');
      }

      console.log('SCAN: Image captured successfully, verifying candidate...');

      // Send to backend with location data and punch code for candidate verification
      const detectionResult = await FaceDetectionService.detectFaces(
        imageBase64,
        punchCode,
        location.coords.latitude,
        location.coords.longitude,
      );

      console.log('SCAN: Detection result:', detectionResult);

      const faces = detectionResult.faces || [];
      const backendResponse = detectionResult.backendResponse;
      const isSuccess = detectionResult.success;

      console.log(`SCAN: Backend returned ${faces.length} verified candidates`);
      console.log('SCAN: Backend response:', backendResponse);
      setDetectedFaces(faces);

      // Show backend response popup
      if (backendResponse) {
        const message = backendResponse.message || 'Verification completed';
        const status = backendResponse.status || 'unknown';
        
        if (status === 'success' || isSuccess) {
          alert(`✅ ${message}\n\nFaces detected: ${faces.length}`);
          
          // Mark attendance entry
          const entryResult = await AttendanceService.markEntry(location, faces, punchCode, imageBase64);
          console.log('Entry result:', entryResult);
          
        } else {
          alert(`❌ ${message}\n\nPlease try again.`);
        }
      } else {
        alert('No response from verification service. Please try again.');
      }

    } catch (error) {
      console.error('Error scanning faces:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Error during face scanning: ${errorMessage}`);
    } finally {
      setIsScanning(false);
    }
    stopCamera();
  };

  const startCamera = async () => {
    try {
      console.log('Starting camera...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        } 
      });
      console.log('Camera stream obtained:', stream);
      setCameraStream(stream);
      setShowCamera(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded, playing...');
          videoRef.current?.play().catch(e => console.error('Error playing video:', e));
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
    setDetectedFaces([]);
  };

  const getStatusColor = () => {
    switch (attendanceStatus) {
      case 'entering':
        return '#4CAF50';
      case 'exiting':
        return '#FF9800';
      case 'inside':
        return '#2196F3';
      default:
        return '#2f97bd';
    }
  };

  const getStatusText = () => {
    switch (attendanceStatus) {
      case 'entering':
        return 'ENTERING DESIGNATED AREA';
      case 'exiting':
        return 'EXITING DESIGNATED AREA';
      case 'inside':
        return 'INSIDE DESIGNATED AREA';
      default:
        return 'DESIGNATED AREA';
    }
  };

  const getCurrentLocationInfo = () => {
    if (location) {
      const info = `Punch Code: ${punchCode || 'Not Set'}
Latitude: ${location.coords.latitude.toFixed(6)}
Longitude: ${location.coords.longitude.toFixed(6)}
Accuracy: ${location.coords.accuracy?.toFixed(2)}m
Timestamp: ${new Date(location.timestamp).toLocaleTimeString()}`;
      alert(info);
    } else {
      alert('Location Info: No location data available');
    }
  };

  if (hasPermission === null) {
    return (
      <div className="attendance-screen">
        <div className="loading-container">
          <h2>Requesting permissions...</h2>
        </div>
      </div>
    );
  }

  if (hasPermission === false) {
    return (
      <div className="attendance-screen">
        <div className="error-container">
          <h2>No access to location</h2>
          <p>Location permission is required for this app to work.</p>
          <button className="btn btn-primary" onClick={initializeApp}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-screen">
      <div className="header">
        <div className="header-title">
          {/* <div className="logo">
            <img src="/images/Frame 3.png" alt="CDIT Logo" />
          </div> */}
          <h1>CDIT Smart - Attendance</h1>
        </div>
        <div className="nav-buttons">
          <button className="nav-button" onClick={() => navigate('/history')}>
            {/* <img src="/images/history.png" alt="history" style={{width: '20px', height: '20px'}} /> */}
            History
          </button>
                    <button className="nav-button" onClick={getCurrentLocationInfo}>
            {/* <img src="/images/info.png" alt="info" style={{width: '25px', height: '25px'}} /> */}
            Info
          </button>
        </div>
      </div>

            <div className="main-content">
        <div className="left-panel">
          <div className="punch-code-input">
            {/* <label className="form-label">Punch Code:</label> */}
            <input
              type="text"
              value={punchCode}
              onChange={(e) => setPunchCode(e.target.value)}
              placeholder="Enter punch code"
              className="form-input"
              autoCapitalize="none"
              autoCorrect="off"
            />
            {location && (
              <div className="location-info" style={{ marginTop: '15px' }}>
                <img src="/images/pin.gif" alt="location pin" style={{ width: '30px', height: '30px', marginRight: '8px', verticalAlign: 'middle' }} />
                Latitude: {location.coords.latitude.toFixed(6)}, 
                Longitude: {location.coords.longitude.toFixed(6)}
              </div>
            )}
          </div>
        </div>

        <div className="right-panel">
          <div className="camera-container">
            {showCamera ? (
              <div className="camera-placeholder">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="camera-overlay">
                  {isScanning ? 'Verifying Employee...' : 'Camera Active'}
                </div>
              </div>
            ) : (
              <div className="camera-placeholder" style={{
                backgroundImage: 'url(/images/bg_4.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}>
                <div style={{
                  backgroundColor: '#c7c3c3b3',
                   // padding: '1px',
                  borderRadius: '15px',
                  color: 'black',
                  textAlign: 'center',
                  marginTop: '250px'
                }}>
                                      {/* <img src="/images/camera.gif" alt="camera ready" style={{width: '80px', height: '80px',marginBottom: '-18px',padding:'3px'}} /> */}

                  <div> 
                    {/* <img src="/images/camera.gif" alt="camera ready" style={{width: '60px', height: '60px',marginBottom: '-18px',padding:'3px'}} /> */}
                    {/* Camera Ready */}
                  </div>
                  {/* <p>Click "Start Camera" to begin face detection</p> */}
                </div>
              </div>
            )}
          </div>

          <div className="controls">
            {!showCamera ? (
              <button className="control-button verify" onClick={startCamera}>
                <img src="/images/camera.gif" alt="scanning" style={{width: '45px', height: '45px'}} />
                Start Camera
              </button>
            ) : (
              <button
                className={`control-button verify ${isScanning ? 'disabled' : ''}`}
                onClick={scanFaces}
                disabled={isScanning}
              >
                {isScanning ? (
                  <img src="/images/security.png" alt="scanning" style={{width: '45px', height: '45px'}} />
                ) : (
                  <img src="/images/security.png" alt="scanning" style={{width: '45px', height: '45px'}} />
                )}
                {isScanning ? 'Verifying...' : 'Verify Employee'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceScreen; 